/**
 * Shared empty / loading / error fragments — keeps page code uncluttered
 * and ensures the same shapes appear everywhere (design book §16).
 */

import { type ReactNode } from "react";
import { AlertCircle, Inbox, Loader2 } from "lucide-react";
import { Button } from "@vseh/ui";

import { useT } from "../i18n";


export function LoadingBlock({ label }: { label?: string }): JSX.Element {
  const t = useT();
  return (
    <div className="flex items-center gap-2 px-1 py-2 text-sm text-base-500">
      <Loader2 aria-hidden="true" className="size-4 animate-spin" />
      <span>{label ?? t("states.loading")}</span>
    </div>
  );
}


export function EmptyBlock({
  title, description, action,
}: { title: string; description?: string; action?: ReactNode }): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-base-300 bg-base-50 px-4 py-10 text-center">
      <Inbox aria-hidden="true" className="size-8 text-base-300" />
      <div>
        <div className="font-display text-lg font-semibold text-base-900">{title}</div>
        {description && (
          <div className="mt-1 max-w-md text-sm text-base-600">{description}</div>
        )}
      </div>
      {action}
    </div>
  );
}


export function ErrorBlock({
  error, onRetry,
}: { error: Error; onRetry?: () => void }): JSX.Element {
  const t = useT();
  return (
    <div className="flex items-start gap-3 rounded-lg border border-danger-200 bg-danger-50 p-4">
      <AlertCircle aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-danger-700" />
      <div className="min-w-0 flex-1">
        <div className="font-medium text-danger-800">{t("states.error.title")}</div>
        <div className="mt-1 break-words text-sm text-danger-700">{error.message}</div>
      </div>
      {onRetry && (
        <Button variant="ghost" size="sm" onClick={onRetry}>{t("states.error.retry")}</Button>
      )}
    </div>
  );
}
